import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient, getAgencies } from "../../../data";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import {
  CustomDataInput,
  FormInput,
  SocialMediaInput,
} from "../../../components";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  User,
  Mail,
  Phone,
  Building2,
  CreditCard,
  MapPin,
  Briefcase,
  Share2,
  Database,
  Percent,
  FileText,
} from "lucide-react";

export function ClientAddScreen() {
  const { data, isLoading } = useQuery({
    queryKey: ["agencies"],
    queryFn: getAgencies,
  });
  const pageSettings = {
    title: "Create Client",
    actions: [
      {
        name: "Go To List",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate("/clients");
        },
      },
    ],
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ 
    mode: "onChange",
    defaultValues: {
      isPrepareTheInvoiceIndividually: true,
    },
  });

  const watchIsHaveAgency = watch("isHaveAgency");
  const mutation = useMutation({
    mutationKey: ["createClient"],
    mutationFn: (data) => {
      return createClient(data);
    },
    onSuccess: (response) => {
      navigate("/clients");
    },
  });

  const onSubmit = async (data) => {
    mutation.mutate({
      email: data.email?.trim(),
      name: data.name?.trim(),
      phoneNumber: data.phoneNumber?.trim(),
      billingName: data.billingName?.trim(),
      bankName: data.bankName?.trim(),
      IBAN: data.IBAN?.trim(),
      SWIFT: data.SWIFT?.trim(),
      customData: data.customData,
      isHaveAgency: data.isHaveAgency,
      manager: data.manager?.trim(),
      socialMediaAccounts: data.socialMediaAccounts,
      agencyId: data.agencyId,
      lucaId: data.lucaId?.trim(),
      lucaAccountName: data.lucaAccountName?.trim(),
      currency: data.currency?.trim(),
      country: data.country?.trim(),
      newTvAccountManager: data.newTvAccountManager?.trim(),
      newtvCommissionPercentage: data.newtvCommissionPercentage ? parseFloat(data.newtvCommissionPercentage) : null,
      isPrepareTheInvoiceIndividually: data.isPrepareTheInvoiceIndividually === true,
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AppLayout title={pageSettings.title} actions={pageSettings.actions}>
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5 text-blue-600" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter client name"
                    className="mt-1.5"
                    {...register("name", {
                      required: "Name is required",
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="client@example.com"
                    className="mt-1.5"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email format",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="text"
                    placeholder="+1 234 567 8900"
                    className="mt-1.5"
                    {...register("phoneNumber", {
                      required: "Phone Number is required",
                    })}
                  />
                  {errors.phoneNumber && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="billingName" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Billing Name *
                  </Label>
                  <Input
                    id="billingName"
                    type="text"
                    placeholder="Billing name"
                    className="mt-1.5"
                    {...register("billingName", {
                      required: "Billing Name is required",
                    })}
                  />
                  {errors.billingName && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.billingName.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Location & Manager */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  Location & Management
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="country" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Country *
                  </Label>
                  <Input
                    id="country"
                    type="text"
                    placeholder="Country"
                    className="mt-1.5"
                    {...register("country", {
                      required: "Country is required",
                    })}
                  />
                  {errors.country && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.country.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="currency" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Currency *
                  </Label>
                  <Input
                    id="currency"
                    type="text"
                    placeholder="EUR, USD, etc."
                    className="mt-1.5"
                    {...register("currency", {
                      required: "Currency is required",
                    })}
                  />
                  {errors.currency && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.currency.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="newTvAccountManager"
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    New TV Account Manager *
                  </Label>
                  <Input
                    id="newTvAccountManager"
                    type="text"
                    placeholder="Manager name"
                    className="mt-1.5"
                    {...register("newTvAccountManager", {
                      required: "New TV Account Manager is required",
                    })}
                  />
                  {errors.newTvAccountManager && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.newTvAccountManager.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Luca Account */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="h-5 w-5 text-emerald-600" />
                  Luca Account
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="lucaId" className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Luca ID
                  </Label>
                  <Input
                    id="lucaId"
                    type="text"
                    placeholder="Luca ID"
                    className="mt-1.5 font-mono"
                    {...register("lucaId")}
                  />
                  {errors.lucaId && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.lucaId.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="lucaAccountName"
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    Luca Account Name
                  </Label>
                  <Input
                    id="lucaAccountName"
                    type="text"
                    placeholder="Luca Account Name"
                    className="mt-1.5"
                    {...register("lucaAccountName")}
                  />
                  {errors.lucaAccountName && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.lucaAccountName.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Commission Settings */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Percent className="h-5 w-5 text-green-600" />
                  Commission Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label
                    htmlFor="newtvCommissionPercentage"
                    className="flex items-center gap-2"
                  >
                    <Percent className="h-4 w-4" />
                    NewTV Commission Percentage (%)
                  </Label>
                  <Input
                    id="newtvCommissionPercentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="e.g. 15"
                    className="mt-1.5"
                    {...register("newtvCommissionPercentage")}
                  />
                  {errors.newtvCommissionPercentage && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.newtvCommissionPercentage.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isPrepareTheInvoiceIndividually"
                    name="isPrepareTheInvoiceIndividually"
                    defaultChecked={true}
                    onCheckedChange={(checked) => {
                      setValue("isPrepareTheInvoiceIndividually", checked);
                    }}
                    {...register("isPrepareTheInvoiceIndividually")}
                  />
                  <Label
                    htmlFor="isPrepareTheInvoiceIndividually"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Prepare Invoice Individually (Bireysel Fatura)
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Agency Information */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5 text-orange-600" />
                  Agency Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isHaveAgency"
                    name="isHaveAgency"
                    onCheckedChange={(data) => {
                      setValue("isHaveAgency", data);
                    }}
                    {...register("isHaveAgency")}
                  />
                  <Label
                    htmlFor="isHaveAgency"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Client has an agency
                  </Label>
                </div>

                {watchIsHaveAgency && (
                  <div>
                    <Label>Select Agency</Label>
                    <Select {...register("agencyId")}>
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select an agency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Agencies</SelectLabel>
                          {data.data.agencies.map((agency) => (
                            <SelectItem
                              value={agency.id.toString()}
                              key={agency.id}
                            >
                              {agency.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Banking Details */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5 text-cyan-600" />
                  Banking Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="bankName" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Bank Name *
                  </Label>
                  <Input
                    id="bankName"
                    type="text"
                    placeholder="Bank name"
                    className="mt-1.5"
                    {...register("bankName", {
                      required: "Bank Name is required",
                    })}
                  />
                  {errors.bankName && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.bankName.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="IBAN" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    IBAN *
                  </Label>
                  <Input
                    id="IBAN"
                    type="text"
                    placeholder="IBAN"
                    className="mt-1.5 font-mono"
                    {...register("IBAN", {
                      required: "IBAN is required",
                    })}
                  />
                  {errors.IBAN && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.IBAN.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="SWIFT" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    SWIFT *
                  </Label>
                  <Input
                    id="SWIFT"
                    type="text"
                    placeholder="SWIFT/BIC code"
                    className="mt-1.5 font-mono"
                    {...register("SWIFT", {
                      required: "SWIFT is required",
                    })}
                  />
                  {errors.SWIFT && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.SWIFT.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/30 dark:to-rose-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Share2 className="h-5 w-5 text-pink-600" />
                  Social Media Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <SocialMediaInput
                  onDataChanged={(data) => {
                    console.log("data", data);
                    setValue("socialMediaAccounts", data);
                  }}
                />
                {errors.socialMediaAccounts && (
                  <p className="text-sm text-destructive mt-2">
                    {errors.socialMediaAccounts.message}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Custom Data */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Database className="h-5 w-5 text-violet-600" />
                  Custom Data
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <CustomDataInput
                  onDataChanged={(data) => {
                    setValue("customData", data);
                  }}
                />
                {errors.customData && (
                  <p className="text-sm text-destructive mt-2">
                    {errors.customData.message}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/clients")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            Create Client
          </Button>
        </div>
      </form>
    </AppLayout>
  );
}
